import { supabase } from './supabase';

export const CANONICAL_ORIGIN = 'https://www.nurameditation.com';
export const APP_STORE_URL = 'https://apps.apple.com/us/app/nura-adaptive-meditation/id6756171302';

export interface MeditationPreview {
  id: string;
  title: string;
  subtitle: string;
  description: string | null;
  duration_seconds: number;
  narration_voice: string | null;
  category: string | null;
  artwork_path: string | null;
  artwork_thumbnail_path: string | null;
}

export interface MeditationArtwork {
  path: string;
  blurPath?: string;
  width: number;
  height: number;
}

export type MeditationPreviewStatus = 'ready' | 'missing' | 'unavailable';

export interface MeditationPreviewBootstrap {
  id: string;
  status: MeditationPreviewStatus;
  item: MeditationPreview | null;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PREVIEW_BOOTSTRAP_ID = 'nura-meditation-preview-data';
const ARTWORK_BUCKET = 'meditation-artwork';

const FALLBACK_ARTWORK: MeditationArtwork = {
  path: '/og-image.jpg',
  blurPath: '/og-image.jpg',
  width: 1200,
  height: 630,
};

const ARTWORK_BY_ID: Record<string, MeditationArtwork> = {
  'a1b2c3d4-e5f6-7890-abcd-ef1234567890': {
    path: '/meditations/a1b2c3d4-e5f6-7890-abcd-ef1234567890.jpg',
    blurPath: '/meditations/a1b2c3d4-e5f6-7890-abcd-ef1234567890-blur.jpg',
    width: 736,
    height: 1308,
  },
  '7c5d5f42-532d-4eae-8b95-6f3aeb3ee889': {
    path: '/meditations/7c5d5f42-532d-4eae-8b95-6f3aeb3ee889.jpg',
    blurPath: '/meditations/7c5d5f42-532d-4eae-8b95-6f3aeb3ee889-blur.jpg',
    width: 720,
    height: 1280,
  },
};

export function isMeditationID(value: string): boolean {
  return UUID_PATTERN.test(value);
}

export function normalizeMeditationID(value: string): string {
  return value.toLowerCase();
}

export function canonicalMeditationURL(id: string): string {
  return `${CANONICAL_ORIGIN}/meditations/${normalizeMeditationID(id)}`;
}

function publicArtworkURL(storagePath: string | null | undefined): string | null {
  const path = storagePath?.trim() ?? '';
  const supabaseURL = import.meta.env.VITE_SUPABASE_URL;

  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  if (!supabaseURL) return null;

  const encodedPath = path
    .split('/')
    .filter(Boolean)
    .map((component) => encodeURIComponent(component))
    .join('/');

  return `${supabaseURL.replace(/\/$/, '')}/storage/v1/object/public/${ARTWORK_BUCKET}/${encodedPath}`;
}

export function artworkForMeditation(id: string, item?: MeditationPreview | null): MeditationArtwork {
  const artworkPath = publicArtworkURL(item?.artwork_path);
  const thumbnailPath = publicArtworkURL(item?.artwork_thumbnail_path);

  if (artworkPath || thumbnailPath) {
    return {
      path: artworkPath ?? thumbnailPath ?? FALLBACK_ARTWORK.path,
      blurPath: artworkPath && thumbnailPath ? thumbnailPath : undefined,
      width: 720,
      height: 1280,
    };
  }

  return ARTWORK_BY_ID[normalizeMeditationID(id)] ?? FALLBACK_ARTWORK;
}

export function getMeditationPreviewBootstrap(id: string): MeditationPreviewBootstrap | null {
  if (typeof document === 'undefined') return null;

  const element = document.getElementById(PREVIEW_BOOTSTRAP_ID);
  if (!element?.textContent) return null;

  try {
    const payload = JSON.parse(element.textContent) as Partial<MeditationPreviewBootstrap>;
    if (payload.id !== normalizeMeditationID(id)) return null;
    if (!payload.status || !['ready', 'missing', 'unavailable'].includes(payload.status)) {
      return null;
    }

    return {
      id: payload.id,
      status: payload.status,
      item: payload.item ?? null,
    } as MeditationPreviewBootstrap;
  } catch {
    return null;
  }
}

export async function fetchMeditationPreview(id: string): Promise<MeditationPreview | null> {
  if (!supabase) {
    throw new Error('Supabase is not configured');
  }

  const { data, error } = await supabase
    .from('meditations')
    .select('id,title,subtitle,description,duration_seconds,narration_voice,category,artwork_path,artwork_thumbnail_path')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data as MeditationPreview | null;
}

export function formatDuration(seconds: number): string {
  return `${Math.max(1, Math.floor(seconds / 60))} min`;
}

export function formatCategory(category: string | null): string {
  if (!category) return 'Meditation';
  return category.charAt(0).toUpperCase() + category.slice(1);
}

export function formatVoice(voice: string | null): string {
  if (!voice || voice === 'none') return 'No voice';
  return voice.charAt(0).toUpperCase() + voice.slice(1);
}
